import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import AuthenticatedLayout from '../../components/layouts/authenticated_layout'
import {
  RecruitmentFormLayout,
  RecruitmentFormData,
} from './components/RecruitmentFormLayout'
import {
  getKyousouApiClient,
  ApiErrorResponse,
  handleError,
} from '../../services/network'
import { useSnackBar } from '../../providers/snack_bar_provider'
import Breadcrumb from '../../components/common/breadcrumb'

/**
 * 共創パートナー募集作成ページ
 * 新しいパートナー募集を作成するためのフォームを提供
 */
const RecruitmentCreatePage: React.FC = () => {
  // ナビゲーション用フック
  const navigate = useNavigate()
  // APIクライアントの取得
  const apiClient = getKyousouApiClient()
  // スナックバー（通知）表示用フック
  const { showSnackBar } = useSnackBar()

  // フォーム管理用フック（react-hook-form）
  const {
    register, // フォームフィールドの登録
    handleSubmit, // フォーム送信処理
    watch, // フォームの値を監視
    setValue, // フォームの値を設定
    control, // フォームコントロール
    formState: { errors, isSubmitting }, // エラー状態と送信中状態
  } = useForm<RecruitmentFormData>({
    // フォームの初期値設定
    defaultValues: {
      title: '', // 募集タイトル
      description: '', // 募集説明
      requirements: '', // 応募条件
      legalMatters: '', // 法的注意事項
      thumbnailUrl: '', // サムネイル画像URL
      status: 'published', // ステータス（デフォルトで公開）
      scheduledDate: '', // 予約投稿日時
      sdgsCategories: [], // SDGsカテゴリ（空配列）
    },
  })

  /**
   * 公開時の処理
   * @param data フォームデータ
   */
  const onSubmit = async (data: RecruitmentFormData) => {
    // 予約投稿日をY-m-d H:i:s形式に変換（日付のみの場合は0時0分に設定）
    let formattedScheduledDate = undefined
    if (data.status === 'scheduled' && data.scheduledDate) {
      // YYYY-MM-DD形式の日付をYYYY-MM-DD 00:00:00形式に変換
      formattedScheduledDate = `${data.scheduledDate} 00:00:00`
    }

    // APIを呼び出してパートナー募集を作成
    const response = await apiClient.partnerRecruitments
      .partnerRecruitmentsCreate({
        title: data.title,
        description: data.description,
        requirements: data.requirements || '',
        legalMatters: data.legalMatters || '',
        thumbnailUrl: data.thumbnailUrl || undefined,
        status: data.status || 'published',
        sdgsCategories: data.sdgsCategories,
        ...(formattedScheduledDate
          ? { scheduledDate: formattedScheduledDate }
          : {}),
      })
      .catch((e: ApiErrorResponse) => {
        handleError(e, showSnackBar)
        return null
      })

    if (response) {
      // 作成成功時のメッセージと遷移先をステータスに応じて変更
      if (data.status === 'scheduled') {
        showSnackBar(['予約投稿を保存しました'], 'success')
        navigate('/recruitment/manage')
      } else {
        showSnackBar(['募集を公開しました'], 'success')
        navigate('/recruitment/manage')
      }
    }
  }

  /**
   * 下書き保存時の処理
   * @param data フォームデータ
   */
  const onSaveDraft = async (data: RecruitmentFormData) => {
    // APIを呼び出してパートナー募集を下書き保存
    const response = await apiClient.partnerRecruitments
      .partnerRecruitmentsCreate({
        title: data.title || '',
        description: data.description || '',
        requirements: data.requirements || '',
        legalMatters: data.legalMatters || '',
        thumbnailUrl: data.thumbnailUrl || undefined,
        status: 'draft',
        sdgsCategories: data.sdgsCategories || [],
      })
      .catch((e: ApiErrorResponse) => {
        handleError(e, showSnackBar)
        return null
      })

    if (response) {
      // 下書き保存成功時、投稿済み一覧ページに遷移
      showSnackBar(['下書きを保存しました'], 'success')
      navigate('/recruitment/manage')
    }
  }

  /**
   * キャンセルボタンクリック時の処理
   * 募集一覧ページに戻る
   */
  const handleCancel = () => {
    navigate('/recruitment')
  }

  /**
   * 下書き保存ボタンクリック時の処理
   * バリデーションをスキップして下書き保存を実行
   */
  const handleSaveDraft = () => {
    // フォームの現在の値を取得してバリデーションなしで下書き保存
    const currentValues = watch()
    onSaveDraft(currentValues)
  }

  return (
    <AuthenticatedLayout>
      <Breadcrumb
        items={[
          { label: 'ホーム', path: '/dashboard' },
          { label: '自社の募集', path: '/recruitment/manage' },
          { label: '新規作成' },
        ]}
      />

      {/* 募集作成フォームレイアウト */}
      <RecruitmentFormLayout
        title="共創パートナー募集"
        onSubmit={handleSubmit(onSubmit)}
        register={register}
        errors={errors}
        isSubmitting={isSubmitting}
        onCancel={handleCancel}
        onSaveDraft={handleSaveDraft}
        watch={watch}
        setValue={setValue}
        control={control}
      />
    </AuthenticatedLayout>
  )
}

export default RecruitmentCreatePage
